import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useContent, deepMerge } from "@/content/ContentContext";

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(
    () => localStorage.getItem("umagazin-lang") || "it"
  );
  useEffect(() => {
    localStorage.setItem("umagazin-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const { textOverrides } = useContent();
  const t = useMemo(() => deepMerge(texts[lang], textOverrides[lang] || {}), [lang, textOverrides]);
  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);

export const texts = {
  it: {
    nav: {
      home: "Home",
      locale: "Il locale",
      menu: "Menu",
      servizi: "Servizi",
      gallery: "Gallery",
      contatti: "Contatti",
      book: "Prenota",
    },
    hero: {
      eyebrow: "Aperitivi nostrani e ristorante",
      titleA: "Sapori di Sardegna,",
      titleB: "nel cuore di Carloforte.",
      sub: "Dettagli d'altri tempi, un calice di vino isolano e una tavola dove fermarsi volentieri: qui si inizia con l'aperitivo e si finisce con una cena.",
      ctaPrimary: "Prenota su WhatsApp",
      ctaSecondary: "Scopri il menu",
      scroll: "Scorri",
      slides: [
        "Vini sardi, pietra viva e luce calda",
        "Il servizio al tavolo, un calice alla volta",
        "La cucina d'altri tempi e le sedie colorate",
        "Culurgiones al nero di seppia, in terracotta",
        "Il dehors tra le piante, all'ora dell'aperitivo",
        "Tappi di sughero nella ceramica",
        "Cozze appena saltate, profumo di mare",
        "Cozze in terracotta, pane carasau e un calice di bianco",
      ],
    },
    badges: {
      lgbtq: "LGBTQ+ Friendly",
      animal: "Animal Friendly",
      wine: "Selezione dei migliori vini del territorio",
      fish: "Crudi di pesce a Chilometro Zero",
    },
    locale: {
      label: "Il locale",
      title: "Dove il tempo rallenta e la tavola si allunga",
      p1: "Pietra a vista, legno vissuto, nasse appese alle pareti e una Vespa d'epoca Piaggio del '57 ferma tra i tavoli: da U Magazin il tempo rallenta. Ogni dettaglio racconta un'idea di ospitalità fatta di cose vere, scelte una a una.",
      p2: "Ci si siede per un aperitivo da condividere e si resta volentieri per cena. La cucina è nostrana e genuina, come quella di un tempo, come quella della nonna: prodotti locali a filiera corta, crudi di mare, ostriche e gamberi, antipasti della tradizione e una carta dei vini che attraversa le cantine dell'isola.",
      p3: "A guidarti c'è uno staff che conosce e ama questi sapori: chiedi, assaggia, lasciati consigliare. Al resto pensa la tavola.",
      welcomeLabel: "La nostra idea di accoglienza",
      welcomeText:
        "Ci piacciono le tavole che uniscono, le storie diverse e la libertà di essere sé stessi. Da U Magazin ogni persona è benvenuta.",
      altFacade: "L'ingresso di U Magazin alla sera, a Carloforte",
      altRoom: "La sala in pietra di U Magazin",
      altCorner: "Un angolo del locale con vista verso il mare",
      altCouple: "Il dehors di U Magazin tra le piante, con ospiti ai tavoli alla sera",
    },
    sapori: {
      label: "Sapori e condivisione",
      title: "Il meglio dell'isola, nel piatto e nel calice",
      intro:
        "Mare, terra e cantine sarde, serviti con cura e passione. Una tavola pensata per essere condivisa, dall'inizio alla fine.",
      items: [
        {
          title: "Prodotti locali a filiera corta",
          text: "Formaggi, salumi e miele dell'isola, scelti tra piccoli produttori: il territorio servito in tavola, da dividere con chi vuoi.",
        },
        {
          title: "Crudi di mare, ostriche e gamberi",
          text: "Pesce tagliato al momento, ostriche e gamberi, insieme agli antipasti della tradizione. Il mare di San Pietro, senza filtri.",
        },
        {
          title: "Vini di Sardegna",
          text: "Degustazioni di vini di Sardegna in loco, comprese piccole cantine di nicchia con produzioni limitate. Le bottiglie si possono acquistare anche da asporto: chiedici un consiglio, ci piace indicare la rotta.",
        },
        {
          title: "Cena con prodotti a Chilometro Zero",
          text: "Cucina nostrana e genuina, come quella di un tempo: piatti della tradizione con materie prime dell'isola, da piccoli produttori e pescatori locali.",
        },
        {
          title: "Prodotti artigianali di nicchia",
          text: "Piccole produzioni di artigiani del territorio: i dolci fatti in casa, preparati come una volta e in quantità limitate.",
        },
      ],
      note: "Qui si inizia con l'aperitivo e si finisce con una cena.",
    },
    servizi: {
      label: "Servizi",
      title: "U Magazin, anche fuori dal locale.",
      intro: "Tre modi per portarci con te: un regalo che sa di casa, la nostra cucina a domicilio e la sala tutta per te. Dettagli e preventivo su richiesta.",
      note: "Dettagli e preventivo su richiesta: scrivici su WhatsApp o chiamaci, troviamo insieme la soluzione giusta.",
      cta: "Scopri i servizi",
      ctaAll: "Vedi tutti i servizi",
      ask: "Richiedi su WhatsApp",
      call: "Chiamaci",
      pageTitle: "Servizi su misura, con la stessa cura della nostra tavola.",
      pageIntro: "Dal regalo pensato all'ultimo momento alla cena a casa tua, fino alla sala riservata per una festa: ogni servizio nasce dalla nostra cucina nostrana e genuina e si costruisce insieme a te.",
      items: {
        regalo: {
          name: "Regala una cena",
          kicker: "Buono regalo su misura",
          desc: "Un'idea semplice e sempre giusta: regali una cena da U Magazin e noi prepariamo il buono con il nome di chi lo riceve e il tuo messaggio. Scegli tu l'importo o la formula, dall'aperitivo nostrano alla cena completa.",
          detail: "Il buono viene emesso in locale o concordato via WhatsApp, ha validità di un anno e si può usare anche in più volte.",
          alt: "Tavola imbandita con calici di vino a U Magazin",
        },
        domicilio: {
          name: "Porta a casa tua il Magazin",
          kicker: "Cena a domicilio",
          desc: "La nostra cucina arriva dove vuoi tu: fregula, crudi di mare, antipasti della tradizione e dolci di casa, preparati al momento e consegnati pronti da servire. Perfetto per una cena in famiglia o una serata tra amici senza pensieri.",
          detail: "Zone di consegna, orari e proposte del giorno si concordano su richiesta, con qualche giorno di preavviso per i menù più articolati.",
          alt: "Piatto della tradizione servito nella terracotta di U Magazin",
        },
        sala: {
          name: "Prenota la sala in esclusiva",
          kicker: "Eventi privati",
          desc: "La sala in pietra tutta per te: compleanni, anniversari, cene aziendali, presentazioni e feste. Allestimento su misura, menù dedicato e la nostra selezione di vini sardi, nel cuore di Carloforte.",
          detail: "Disponibilità, capienza e menù dedicato su richiesta: raccontaci che serata hai in mente e ti prepariamo un preventivo.",
          alt: "La sala in pietra di U Magazin con sedie rosse",
        },
      },
    },
    signature: {
      label: "Il piatto più amato",
      badge: "Specialità della casa",
      title: "Fregula sarda",
      p1: "È il piatto che i nostri clienti ordinano più di ogni altro: fregula sarda tostata, cotta nel sugo con gambero rosso, aragosta e frutti di mare del nostro mare, con un filo di prezzemolo fresco. Servita nella terracotta, come si faceva un tempo.",
      p2: "Cucina nostrana e autentica: pochi ingredienti, tutti scelti a filiera corta, e il tempo giusto perché la fregula assorba tutto il sapore del mare.",
      quote: "Chi la assaggia la prima volta, la riordina la seconda.",
      cta: "Guarda il menù",
      alt: "Fregula sarda con frutti di mare servita nella terracotta di U Magazin",
    },
    formule: {
      label: "Le nostre formule",
      title: "Tre modi di vivere la nostra tavola",
      cta: "Scopri il menù",
      placeholder: "Il menù completo sarà disponibile a breve.",
      items: {
        aperitivo: {
          name: "Aperitivo nostrano",
          kicker: "Prodotti locali a filiera corta",
          desc: "Vini di Sardegna in degustazione accompagnati da prodotti locali a filiera corta: crudi di mare, ostriche, gamberi e antipasti della tradizione.",
        },
        cena: {
          name: "Cena",
          kicker: "Menù alla carta",
          desc: "Cucina nostrana e genuina, come quella di un tempo: primi della tradizione, crudi di mare, piatti del giorno e dolci di casa, con una carta dei vini che attraversa le cantine sarde.",
        },
        catering: {
          name: "Catering da asporto",
          kicker: "Piatti dedicati in fase di elaborazione",
          desc: "Portiamo la nostra cucina nostrana e genuina anche fuori dal locale. I piatti dedicati al catering sono in fase di elaborazione: scrivici per conoscere le proposte disponibili.",
        },
      },
    },
    gallery: {
      label: "Gallery",
      title: "Interni, tavole e dettagli d'altri tempi",
      close: "Chiudi",
      showAll: "Mostra tutte le foto",
      showLess: "Mostra meno",
      prev: "Foto precedente",
      next: "Foto successiva",
    },
    reviews: {
      label: "Dicono di noi",
      title: "Le parole di chi si è seduto alla nostra tavola",
      reviewsOn: "recensioni su",
      prev: "Recensione precedente",
      next: "Recensione successiva",
      swipeHint: "Scorri o trascina",
    },
    contatti: {
      label: "Contatti e prenotazioni",
      title: "Ci vediamo da U Magazin.",
      text: "Per un aperitivo che finisce a cena, una tavola da condividere o una serata tra amici: scrivici, ti rispondiamo noi.",
      cta: "Prenota su WhatsApp",
      hint: "Indica in chat data, orario e numero di persone: la prenotazione viene confermata dal locale tramite WhatsApp.",
      phoneLabel: "Telefono",
      addressLabel: "Dove siamo",
      hoursLabel: "Orari",
      hoursPlaceholder: "Orari in aggiornamento.",
      instagramPlaceholder: "Profilo Instagram in aggiornamento",
      directions: "Come arrivare",
      altDoor: "L'insegna di U Magazin sopra l'ingresso",
    },
    menu: {
      label: "Il menu",
      title: "Cosa trovi da U Magazin",
      banner:
        "Il nostro menu è in fase di aggiornamento: le voci qui sotto sono segnaposto della versione di lavoro.",
      placeholderItem: "Voce in aggiornamento",
      placeholderDesc: "Descrizione in arrivo",
      formulaNote: "L'aperitivo da U Magazin si accompagna sempre a prodotti locali a filiera corta, crudi di mare o antipasti della tradizione: il calice, da solo, non è previsto.",
      download: "Scarica il menu",
      backHome: "Torna alla home",
      categories: {
        antipasti: "Antipasti della tradizione",
        mare: "Crudi di mare, ostriche e gamberi",
        vini: "Vini di Sardegna",
        drink: "Cocktail e birre",
        dolci: "Dolci",
      },
    },
    footer: {
      tagline: "Sapori di Sardegna, nel cuore di Carloforte.",
      rights: "© 2026 U Magazin · Carloforte",
      navigate: "Navigazione",
      contacts: "Contatti",
    },
  },
  en: {
    nav: {
      home: "Home",
      locale: "The place",
      menu: "Menu",
      servizi: "Services",
      gallery: "Gallery",
      contatti: "Contacts",
      book: "Book",
    },
    hero: {
      eyebrow: "Local aperitivi and restaurant",
      titleA: "Flavours of Sardinia,",
      titleB: "in the heart of Carloforte.",
      sub: "Details from another time, a glass of island wine and a table worth lingering at: here you start with an aperitivo and end with dinner.",
      ctaPrimary: "Book on WhatsApp",
      ctaSecondary: "Discover the menu",
      scroll: "Scroll",
      slides: [
        "Sardinian wines, bare stone and warm light",
        "Table service, one glass at a time",
        "The old-time kitchen and the coloured chairs",
        "Squid-ink culurgiones, served in terracotta",
        "The terrace among the plants, at aperitivo hour",
        "Wine corks in ceramic",
        "Mussels just sautéed, the scent of the sea",
        "Mussels in terracotta, carasau bread and a glass of white",
      ],
    },
    badges: {
      lgbtq: "LGBTQ+ Friendly",
      animal: "Animal Friendly",
      wine: "A selection of the territory's finest wines",
      fish: "Zero-kilometre raw fish",
    },
    locale: {
      label: "The place",
      title: "Where time slows down and the table stretches on",
      p1: "Exposed stone, well-worn wood, fishing creels on the walls and a vintage 1957 Piaggio Vespa parked among the tables: at U Magazin, time slows down. Every detail tells of a hospitality made of real things, chosen one by one.",
      p2: "You sit down for an aperitivo to share and gladly stay for dinner. The kitchen is homely and genuine, like the food of another time, like grandma's: local products from a short supply chain, raw seafood, oysters and prawns, traditional starters and a wine list that crosses the island's cellars.",
      p3: "Guiding you is a staff that knows and loves these flavours: ask, taste, let us advise you. The table takes care of the rest.",
      welcomeLabel: "Our idea of hospitality",
      welcomeText:
        "We love tables that bring people together, different stories and the freedom to be yourself. At U Magazin, everyone is welcome.",
      altFacade: "The entrance of U Magazin in the evening, in Carloforte",
      altRoom: "The stone dining room of U Magazin",
      altCorner: "A corner of the venue looking towards the sea",
      altCouple: "The outdoor terrace of U Magazin among the plants, with guests at the tables in the evening",
    },
    sapori: {
      label: "Flavours and sharing",
      title: "The best of the island, on the plate and in the glass",
      intro:
        "Sea, land and Sardinian cellars, served with care and passion. A table made to be shared, from start to finish.",
      items: [
        {
          title: "Local produce, short supply chain",
          text: "Cheeses, cured meats and honey from the island, chosen from small producers: the territory served at the table, to share with whoever you like.",
        },
        {
          title: "Raw seafood, oysters and prawns",
          text: "Fish cut to order, oysters and prawns, alongside traditional starters. The sea of San Pietro, unfiltered.",
        },
        {
          title: "Wines of Sardinia",
          text: "Sardinian wine tastings on site, including small niche cellars with limited production. Bottles are also available to take away: ask us for advice, we like to point the way.",
        },
        {
          title: "Dinner with zero-kilometre produce",
          text: "Homely, genuine cooking like the food of another time: traditional dishes with island ingredients, from small producers and local fishermen.",
        },
        {
          title: "Niche artisan products",
          text: "Small productions by local artisans: homemade desserts, prepared the old way and in limited quantities.",
        },
      ],
      note: "Here you start with an aperitivo and end with dinner.",
    },
    servizi: {
      label: "Services",
      title: "U Magazin, beyond the venue too.",
      intro: "Three ways to take us with you: a gift that tastes of home, our kitchen delivered to your door and the dining room all to yourself. Details and quote on request.",
      note: "Details and quote on request: write to us on WhatsApp or call us, we will find the right solution together.",
      cta: "Discover the services",
      ctaAll: "See all services",
      ask: "Ask on WhatsApp",
      call: "Call us",
      pageTitle: "Tailored services, with the same care as our table.",
      pageIntro: "From a last-minute gift to dinner at your place, all the way to the dining room reserved for a party: every service comes from our homely, genuine kitchen and is built together with you.",
      items: {
        regalo: {
          name: "Gift a dinner",
          kicker: "Tailored gift voucher",
          desc: "A simple idea that is always right: you gift a dinner at U Magazin and we prepare the voucher with the name of the person receiving it and your message. You choose the amount or the formula, from the local aperitivo to a full dinner.",
          detail: "The voucher is issued at the venue or arranged via WhatsApp, is valid for one year and can also be used over several visits.",
          alt: "Table set with wine glasses at U Magazin",
        },
        domicilio: {
          name: "Take U Magazin home",
          kicker: "Dinner delivered",
          desc: "Our kitchen comes wherever you are: fregula, raw seafood, traditional starters and homemade desserts, prepared to order and delivered ready to serve. Perfect for a family dinner or an evening with friends, with no worries.",
          detail: "Delivery areas, times and dishes of the day are arranged on request, with a few days' notice for more elaborate menus.",
          alt: "Traditional dish served in terracotta at U Magazin",
        },
        sala: {
          name: "Book the room exclusively",
          kicker: "Private events",
          desc: "The stone dining room all to yourself: birthdays, anniversaries, company dinners, launches and parties. Tailored set-up, dedicated menu and our selection of Sardinian wines, in the heart of Carloforte.",
          detail: "Availability, capacity and dedicated menu on request: tell us what kind of evening you have in mind and we will prepare a quote.",
          alt: "The stone dining room of U Magazin with red chairs",
        },
      },
    },
    signature: {
      label: "Our guests' favourite",
      badge: "House speciality",
      title: "Sardinian fregula",
      p1: "It is the dish our guests order more than any other: toasted Sardinian fregula, simmered in a sauce with red prawn, lobster and seafood from our waters, with a touch of fresh parsley. Served in terracotta, the way it used to be done.",
      p2: "Homely, authentic cooking: few ingredients, all sourced from a short supply chain, and the time it takes for the fregula to soak up the whole flavour of the sea.",
      quote: "Whoever tries it the first time, orders it again the second.",
      cta: "See the menu",
      alt: "Sardinian fregula with seafood served in terracotta at U Magazin",
    },
    formule: {
      label: "Our formulas",
      title: "Three ways to enjoy our table",
      cta: "Discover the menu",
      placeholder: "The full menu will be available shortly.",
      items: {
        aperitivo: {
          name: "Local Aperitivo",
          kicker: "Local produce, short supply chain",
          desc: "Sardinian wines to taste, paired with local produce from a short supply chain: raw seafood, oysters, prawns and traditional starters.",
        },
        cena: {
          name: "Dinner",
          kicker: "À la carte menu",
          desc: "Homely, genuine cooking like the food of another time: traditional first courses, raw seafood, dishes of the day and homemade desserts, with a wine list that crosses Sardinian cellars.",
        },
        catering: {
          name: "Takeaway catering",
          kicker: "Dedicated dishes in preparation",
          desc: "We bring our genuine home cooking outside the venue too. The dishes dedicated to catering are still being developed: write to us to find out what is available.",
        },
      },
    },
    gallery: {
      label: "Gallery",
      title: "Interiors, tables and details from another time",
      close: "Close",
      showAll: "Show all photos",
      showLess: "Show less",
      prev: "Previous photo",
      next: "Next photo",
    },
    reviews: {
      label: "Guests say",
      title: "Words from those who sat at our table",
      reviewsOn: "reviews on",
      prev: "Previous review",
      next: "Next review",
      swipeHint: "Swipe or drag",
    },
    contatti: {
      label: "Contacts and bookings",
      title: "See you at U Magazin.",
      text: "For an aperitivo that ends with dinner, a table to share or an evening with friends: write to us, we reply ourselves.",
      cta: "Book on WhatsApp",
      hint: "Tell us the date, time and number of people in the chat: your booking is confirmed by the venue via WhatsApp.",
      phoneLabel: "Phone",
      addressLabel: "Where we are",
      hoursLabel: "Opening hours",
      hoursPlaceholder: "Hours being updated.",
      instagramPlaceholder: "Instagram profile being updated",
      directions: "Get directions",
      altDoor: "The U Magazin sign above the entrance",
    },
    menu: {
      label: "The menu",
      title: "What you will find at U Magazin",
      banner:
        "Our menu is being updated: the entries below are placeholders of the working version.",
      placeholderItem: "Entry being updated",
      placeholderDesc: "Description coming soon",
      formulaNote: "Aperitivo at U Magazin always comes with local produce from a short supply chain, raw seafood or traditional starters: a glass on its own is not available.",
      download: "Download the menu",
      backHome: "Back to home",
      categories: {
        antipasti: "Traditional starters",
        mare: "Raw seafood, oysters and prawns",
        vini: "Wines of Sardinia",
        drink: "Cocktails and beers",
        dolci: "Desserts",
      },
    },
    footer: {
      tagline: "Flavours of Sardinia, in the heart of Carloforte.",
      rights: "© 2026 U Magazin · Carloforte",
      navigate: "Navigation",
      contacts: "Contacts",
    },
  },
};
